// Effective on/off gates for the SQL-editor language features.
//
// `intellisenseSettings.enabled` is the master switch; the safe-mode and
// per-feature toggles only matter while it is on. Every consumer (SqlMonaco's
// provider registrations, its diagnostics passes, the Monaco option block)
// reads through these helpers instead of the raw flags so "IntelliSense off"
// cannot be undone by a finer toggle that happens to be on.

import type { IntellisenseSettings } from '@/store/appStore';

type Gates = Pick<
  IntellisenseSettings,
  'enabled' | 'autoSuggest' | 'semanticCompletion' | 'hoverAndSignatureHelp' | 'liveDiagnostics'
>;

/** Any completion at all — the provider contributes nothing when false. */
export function isCompletionEnabled(settings: Gates): boolean {
  return settings.enabled;
}

/** The suggest widget may open on its own while typing. */
export function isAutoSuggestEnabled(settings: Gates): boolean {
  return settings.enabled && settings.autoSuggest;
}

/** The schema-aware orchestrator runs (else word-based fallback, or nothing when off). */
export function isSemanticCompletionEnabled(settings: Gates): boolean {
  return settings.enabled && settings.semanticCompletion;
}

/** Hover tooltips, signature help and inlay hints. */
export function isHoverAssistEnabled(settings: Gates): boolean {
  return settings.enabled && settings.hoverAndSignatureHelp;
}

/** Edit-time diagnostic markers (`sql`, `sql-syntax`, `sql-typecheck`). */
export function isLiveDiagnosticsEnabled(settings: Gates): boolean {
  return settings.enabled && settings.liveDiagnostics;
}
